import React, { useEffect, useState } from "react";
import axiosInstance from "../../../../api/axiosInstance";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";


const ServiceCard = ({ service, index, onClick }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.05 }}
      viewport={{ once: true }}
      onClick={onClick}
      className="group relative bg-white rounded-2xl overflow-hidden cursor-pointer border border-gray-100 hover:border-[#0097b2]/30 transition-all duration-300 hover:shadow-xl"
    >
      {/* Image Section */}
      <div className="relative h-48 overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100">
        {service.image ? (
          <img
            src={service.image}
            alt={service.title}
            className="object-cover w-full h-full transform group-hover:scale-110 transition-transform duration-500"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'><rect width='400' height='300' fill='%23F1F5F9'/><text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' font-family='sans-serif' font-size='16' font-weight='600' fill='%2394A3B8'>Service</text></svg>";
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-cyan-50 to-blue-50">
            <div className="w-16 h-16 rounded-full bg-gradient-to-r from-[#134DA7] to-[#0097b2] flex items-center justify-center">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
          </div>
        )}

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

        {/* Popular Badge */}
        <div className="absolute top-3 right-3 px-3 py-1 bg-gradient-to-r from-[#134DA7] to-[#0097b2] text-white text-xs font-bold rounded-full shadow-lg">
          Popular
        </div>
      </div>

      {/* Content Section */}
      <div className="p-5">
        <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-[#0097b2] transition-colors">
          {service.title}
        </h3>

        <p className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-2">
          {service.description || "Professional service designed to meet your specific needs with expert guidance."}
        </p>

        {/* CTA */}
        <div className="flex items-center text-[#0097b2] font-semibold text-sm group-hover:gap-2 gap-1 transition-all">
          <span>Learn More</span>
          <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
        </div>
      </div>

      {/* Hover Border Effect */}
      <div className="absolute inset-0 border-2 border-transparent group-hover:border-[#0097b2]/20 rounded-2xl transition-all duration-300 pointer-events-none"></div>
    </motion.div>
  );
};

const Services = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const res = await axiosInstance.get("/users/services");
        const data = res.data?.services || res.data?.data || [];

        const formatted = data.map((s) => ({
          ...s,
          // Backend already returns a fully built URL in the `image` field
          image: s.image || s.thumbnailUrl || null,
        }));

        setServices(formatted);
      } catch (err) {
        console.error("❌ Error fetching services:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  const handleServiceClick = (id) => {
    navigate(`/services/${id}`);
  };

  // Show only first 8 services
  const popularServices = services.slice(0, 8);

  return (
    <>
    
    <section className="relative bg-white py-10 overflow-hidden">
     

      {/* Subtle Background Pattern */}
      <div className="absolute inset-0 opacity-[0.03]">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, rgb(0, 151, 178) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 relative z-10">
        {/* Header */}
        <div className="flex justify-between items-end mb-6">
          <motion.h2
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-xl md:text-2xl font-semibold text-slate-800"
          >
            Popular Services
          </motion.h2>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <div 
              onClick={() => navigate("/services")}
              className="group flex items-center gap-2 text-[#134DA7] font-semibold hover:text-[#0097b2] transition-colors cursor-pointer"
            >
              View all <ArrowRight size={18} className="transform transition-transform group-hover:translate-x-1" />
            </div>
          </motion.div>
        </div>

        {/* Service Cards Grid */}
        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-[#0097b2] border-t-transparent mb-4"></div>
            <p className="text-gray-500 font-medium">Loading services...</p>
          </div>
        ) : popularServices.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-500 text-lg">No services available at the moment.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 ">
              {popularServices.map((service, index) => (
                <ServiceCard
                  key={service.id || service._id}
                  service={service}
                  index={index}
                  onClick={() => handleServiceClick(service.id || service._id)}
                />
              ))}
            </div>


          </>
        )}
      </div>
    </section>
    </>
  );
};

export default Services;
